import { Button, Popover, TimePicker, Tooltip } from 'antd';
import cn from 'classnames';
import moment from 'moment';
import React, { useMemo } from 'react';

import {
  intervalDateFormat,
  shiftTypeLabels,
  timeFormat,
} from '../../constants';
import styles from '../../ScheduleCalendar.module.scss';
import { getShiftType } from '../../utils/scheduleCalendarUtils';

export const ScheduleTask = ({
  activeIntervalCellKey,
  canAssignManualInterval,
  day,
  editingInterval,
  getIntervalAssignmentPayload,
  isCompactCalendar,
  isManualIntervalAssignment,
  resource,
  task,
  onAssignIntervals,
  onClearIntervalCell,
  onEditingIntervalChange,
  onIntervalCellKeyChange,
  onPerformerActivate,
}) => {
  const fromDate = moment(task.fromDate);
  const toDate = moment(task.toDate);
  const durationHours = toDate.diff(fromDate, 'minutes') / 60;
  const durationText = Number.isInteger(durationHours)
    ? durationHours
    : durationHours.toFixed(1).replace('.', ',');
  const displayDay = moment.isMoment(day) ? day : fromDate;
  const timeText = `${fromDate.format('HH:mm')}-${toDate.format('HH:mm')}`;
  const displayShiftType = getShiftType(task.shiftType, durationHours);
  const shiftLabel =
    shiftTypeLabels[displayShiftType] ||
    shiftTypeLabels[task.shiftType] ||
    task.shiftType ||
    '—';
  const cellKey = `${resource?.id ?? 'resource'}-${
    displayDay?.format(intervalDateFormat) || task.id
  }`;
  const isCellPopoverVisible = activeIntervalCellKey === cellKey;
  const isEditingInterval = editingInterval?.cellKey === cellKey;
  const isManualFilledCell =
    isManualIntervalAssignment && canAssignManualInterval;
  const currentIntervalFrom = fromDate.format('HH:mm');
  const currentIntervalTo = toDate.format('HH:mm');
  const editingIntervalFrom =
    isEditingInterval && editingInterval?.from
      ? editingInterval.from
      : currentIntervalFrom;
  const editingIntervalTo =
    isEditingInterval && editingInterval?.to
      ? editingInterval.to
      : currentIntervalTo;
  const isEditingIntervalChanged =
    isEditingInterval &&
    (editingIntervalFrom !== currentIntervalFrom ||
      editingIntervalTo !== currentIntervalTo);

  const tooltipContent = useMemo(
    () => (
      <div className={styles.compactShiftTooltip}>
        <div>Смена: {shiftLabel}</div>
        <div>
          Интервал: {timeText}, {durationText} ч.
        </div>
      </div>
    ),
    [durationText, shiftLabel, timeText]
  );

  const handleFilledCellKeyDown = (event) => {
    if (!isManualFilledCell) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onIntervalCellKeyChange(cellKey);
  };

  const handlePopoverVisibleChange = (visible) => {
    onIntervalCellKeyChange(visible ? cellKey : null);

    if (!visible || activeIntervalCellKey !== cellKey) {
      onEditingIntervalChange(null);
    }
  };

  const handleStartEditInterval = (event) => {
    event.stopPropagation();
    onEditingIntervalChange({
      cellKey,
      from: currentIntervalFrom,
      to: currentIntervalTo,
    });
  };

  const handleCancelEditInterval = (event) => {
    event.stopPropagation();
    onEditingIntervalChange(null);
  };

  const handleChangeEditIntervalFrom = (_value, timeString) => {
    onEditingIntervalChange((prevValue) => ({
      cellKey,
      from: timeString || prevValue?.from || currentIntervalFrom,
      to: prevValue?.to || currentIntervalTo,
    }));
  };

  const handleChangeEditIntervalTo = (_value, timeString) => {
    onEditingIntervalChange((prevValue) => ({
      cellKey,
      from: prevValue?.from || currentIntervalFrom,
      to: timeString || prevValue?.to || currentIntervalTo,
    }));
  };

  const handleSaveEditInterval = (event) => {
    event.stopPropagation();

    if (
      !resource?.id ||
      !displayDay ||
      !editingIntervalFrom ||
      !editingIntervalTo ||
      !isEditingIntervalChanged
    ) {
      return;
    }

    const date = displayDay.format(intervalDateFormat);
    const payload = getIntervalAssignmentPayload(
      [resource.id],
      [date],
      [date],
      {
        from: editingIntervalFrom,
        to: editingIntervalTo,
      }
    );

    if (!payload) {
      return;
    }

    onEditingIntervalChange(null);
    onIntervalCellKeyChange(null);
    onPerformerActivate(resource.id);
    onAssignIntervals(payload);
  };

  const handleDeleteInterval = (event) => {
    event.stopPropagation();
    onClearIntervalCell({ resource, day: displayDay });
  };

  const popoverContent = (
    <div className={styles.intervalDetails}>
      <div className={styles.intervalDetailsTitle}>
        {resource?.fullName || 'Исполнитель'}
      </div>
      <div className={styles.intervalDetailsRow}>
        {displayDay.format('D MMMM YYYY | dddd')}
      </div>
      <div className={styles.intervalDetailsRow}>Смена: {shiftLabel}</div>
      <div
        className={cn(styles.intervalDetailsRow, {
          [styles.intervalDetailsEditRow]: isEditingInterval,
        })}
      >
        {isEditingInterval ? (
          <>
            <span>Интервал:</span>
            <div className={styles.intervalDetailsTimeFields}>
              <TimePicker
                allowClear={false}
                className={styles.intervalDetailsTimePicker}
                format={timeFormat}
                minuteStep={5}
                value={moment(editingIntervalFrom, timeFormat)}
                onChange={handleChangeEditIntervalFrom}
              />
              <span className={styles.intervalDetailsTimeSeparator}>—</span>
              <TimePicker
                allowClear={false}
                className={styles.intervalDetailsTimePicker}
                format={timeFormat}
                minuteStep={5}
                value={moment(editingIntervalTo, timeFormat)}
                onChange={handleChangeEditIntervalTo}
              />
            </div>
          </>
        ) : (
          <>
            Интервал: {timeText}, {durationText} ч.
          </>
        )}
      </div>
      <div className={styles.intervalDetailsActions}>
        {isEditingInterval ? (
          <>
            <Button
              className={styles.intervalDetailsAction}
              onClick={handleCancelEditInterval}
            >
              Отменить
            </Button>
            <Button
              type="primary"
              className={styles.intervalDetailsAction}
              disabled={!isEditingIntervalChanged}
              onClick={handleSaveEditInterval}
            >
              Сохранить
            </Button>
          </>
        ) : (
          <>
            <Button
              className={styles.intervalDetailsAction}
              onClick={handleStartEditInterval}
            >
              Редактировать
            </Button>
            <Button
              type="danger"
              className={styles.intervalDetailsAction}
              onClick={handleDeleteInterval}
            >
              Удалить
            </Button>
          </>
        )}
      </div>
    </div>
  );
  const shiftCard = (
    <div
      key={task.id}
      className={cn(
        styles.shiftCard,
        styles[`shiftCard_${displayShiftType}`],
        isCompactCalendar && styles.shiftCard_compact,
        isManualFilledCell && styles.intervalAssignmentCellDetails,
        isCellPopoverVisible && styles.intervalAssignmentCellDetailsActive
      )}
      aria-label={`Интервал ${timeText}`}
      role={isManualFilledCell ? 'button' : undefined}
      tabIndex={isManualFilledCell ? 0 : undefined}
      onKeyDown={handleFilledCellKeyDown}
    >
      {!isCompactCalendar && (
        <>
          <span className={styles.shiftTime}>{timeText}</span>
          <span className={styles.shiftDuration}>{durationText} ч.</span>
        </>
      )}
    </div>
  );

  return isManualFilledCell ? (
    <Tooltip
      key={task.id}
      title={isCellPopoverVisible ? '' : tooltipContent}
      placement="top"
    >
      <Popover
        key={task.id}
        trigger="click"
        placement="top"
        visible={isCellPopoverVisible}
        content={popoverContent}
        onVisibleChange={handlePopoverVisibleChange}
      >
        {shiftCard}
      </Popover>
    </Tooltip>
  ) : (
    <Tooltip key={task.id} title={tooltipContent} placement="top">
      {shiftCard}
    </Tooltip>
  );
};

export const EmptyScheduleTask = ({
  getManualIntervalCellProps,
  isCompactCalendar,
  resource,
  day,
}) => {
  const manualIntervalCellProps = getManualIntervalCellProps(resource, day);
  const tooltipContent = (
    <div className={styles.compactShiftTooltip}>
      <div>Смена: —</div>
      <div>Интервал: —</div>
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="top">
      <div
        className={cn(
          styles.shiftCard,
          styles.shiftCard_empty,
          isCompactCalendar && styles.shiftCard_compact,
          manualIntervalCellProps.className
        )}
        aria-hidden={
          manualIntervalCellProps.role === 'button' ? undefined : 'true'
        }
        aria-label={manualIntervalCellProps['aria-label']}
        role={manualIntervalCellProps.role}
        tabIndex={manualIntervalCellProps.tabIndex}
        onClick={manualIntervalCellProps.onClick}
        onKeyDown={manualIntervalCellProps.onKeyDown}
      />
    </Tooltip>
  );
};
